// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatRoom } from '../../../libs/models/conversations/ChatRoom';
import {
  // ConversationInputChange,
  Conversations,
  ContinuationToken,
  ConversationsState,
  // ConversationSystemDescriptionChange,
  ConversationTitleChange,
  initialState,
  UpdateBotStatePayload,
} from '../../../libs/models/conversations/ConversationsState_v2';
import {
  AgentResponseUpdate,
  AIContent,
  ChatMessage,
  IChatMessage,
  IChatStreamingProgressMessage,
  isTextContent,
  ChatMessageList,
  isFunctionResultContent,
} from '../../../libs/models/conversations/ChatMessage';
import { isFunctionCallContent } from '../../../libs/models/agui/FunctionCallContent';
import { IFileAnalyzedCacheResult } from '../../../libs/models/conversations/File';
import { WebSearchSource } from '../../../libs/models/app/Plugin';
import { IMCPTool } from '../../../libs/models/app/mcp/Tool';

export const conversationsSlice = createSlice({
  name: 'conversations/v2',
  initialState,
  reducers: {
    setConversations: (state: ConversationsState, action: PayloadAction<Conversations>) => {
      state.conversations = { ...state.conversations, ...action.payload };
    },
    setCountinuationToken: (state: ConversationsState, action: PayloadAction<ContinuationToken>) => {
      state.continuationToken = action.payload;
    },
    editConversationTitle: (state: ConversationsState, action: PayloadAction<ConversationTitleChange>) => {
      const id = action.payload.id;
      const newTitle = action.payload.newTitle;
      state.conversations[id].title = newTitle;
      // 24.07.11 이름변경시 순서 이동 삭제
      // frontLoadChat(state, id);
    },
    // editConversationInput: (state: ConversationsState, action: PayloadAction<ConversationInputChange>) => {
    //     const id = action.payload.id;
    //     const newInput = action.payload.newInput;
    //     state.conversations[id].input = newInput;
    // },
    // editConversationSystemDescription: (
    //     state: ConversationsState,
    //     action: PayloadAction<ConversationSystemDescriptionChange>,
    // ) => {
    //     const id = action.payload.id;
    //     const newSystemDescription = action.payload.newSystemDescription;
    //     state.conversations[id].systemDescription = newSystemDescription;
    // },
    // editConversationMemoryBalance: (
    //     state: ConversationsState,
    //     action: PayloadAction<{ id: string; memoryBalance: number }>,
    // ) => {
    //     const id = action.payload.id;
    //     const newMemoryBalance = action.payload.memoryBalance;
    //     state.conversations[id].memoryBalance = newMemoryBalance;
    // },
    setSelectedConversation: (state: ConversationsState, action: PayloadAction<{ id: string }>) => {
      state.selectedId = action.payload.id;
    },
    leaveConversation: (state: ConversationsState) => {
      // state.conversations[state.selectedId].messages.data = [] as IChatMessage[];
      state.conversations[state.selectedId].messages.continuationToken = null;
      // state.selectedId = '';
    },
    setChatMessages: (state: ConversationsState, action: PayloadAction<{ id: string; messages: ChatMessageList }>) => {
      const { id: chatroomId, messages: messages } = action.payload;
      //if(!state.conversations[chatroomId].botResponseStatus){
      state.conversations[chatroomId].messages.data = messages.data;
      state.conversations[chatroomId].messages.continuationToken = messages.continuationToken;
      //}
    },
    addChatMessages: (state: ConversationsState, action: PayloadAction<{ id: string; messages: ChatMessageList }>) => {
      const { id: chatroomId, messages: messages } = action.payload;
      state.conversations[chatroomId].messages.data.unshift(...messages.data);
      state.conversations[chatroomId].messages.continuationToken = messages.continuationToken;
    },
    addPluginMessage: (state: ConversationsState, action: PayloadAction<{ message: ChatMessage }>) => {
      const { message: message } = action.payload;
      if (message.sessionId) state.conversations[message.sessionId].messages.data.push(message);
    },
    // toggleMultiUserConversations: (state: ConversationsState) => {
    //     const keys = Object.keys(state.conversations);
    //     keys.forEach((key) => {
    //         if (state.conversations[key].users.length > 1) {
    //             state.conversations[key].hidden = !state.conversations[key].hidden;
    //         }
    //     });
    // },
    addConversation: (state: ConversationsState, action: PayloadAction<ChatRoom>) => {
      const newId = action.payload.id;
      state.conversations = { [newId]: action.payload, ...state.conversations };
      state.selectedId = newId;
    },
    addMessageToConversationFromUser: (
      _state: ConversationsState,
      _action: PayloadAction<{
        message: string;
        chatId: string;
        sessionId: string;
        model: string;
        agentId?: string;
        plugin?: string;
        attachFiles?: IFileAnalyzedCacheResult[];
        cacheMode?: boolean;
        isDraft?: boolean;
        webSearchOn?: boolean;
        webSearchSource?: WebSearchSource;
        mcpTools?: IMCPTool[];
        agentMode?: boolean;
        additionalProperties?: Record<string, object>;
      }>,
    ) => {
      // const { message, chatId } = action.payload;
      // updateConversation(state, chatId, message);
    },

    addMessageToConversationFromServer: (
      state: ConversationsState,
      action: PayloadAction<{ message: IChatMessage }>,
    ) => {
      const { message } = action.payload;
      clearMessageConversation(state, message.id);
      // updateConversation(state, message.chatRoomId, message);
    },
    addMessageToConversation: (state: ConversationsState, action: PayloadAction<{ message: ChatMessage }>) => {
      const { message } = action.payload;
      //clearMessageConversation(state, message[0].sessionId);
      updateConversation(state, message);
      // console.log('updateConversation', message)
    },
    // addStreamingMessageToConversationFromServer: (
    //   state: ConversationsState,
    //   action: PayloadAction<{ message: IChatStreamingMessage }>,
    // ) => {
    //   const { message } = action.payload;
    //   // updateStreamingConversation(state, message);
    // },
    addStreamingMessageToConversationFromServer: (
      state: ConversationsState,
      action: PayloadAction<{ chatRoomId: string; message: AgentResponseUpdate }>,
    ) => {
      const { chatRoomId, message } = action.payload;
      updateStreamingConversation(state, chatRoomId, message);
    },
    clearMessageToConversationFromServer: (state: ConversationsState, action: PayloadAction<{ id: string }>) => {
      const { id: chatId } = action.payload;
      clearMessageConversation(state, chatId);
    },
    retryMessageToConversationFromUser: (
      _state: ConversationsState,
      _action: PayloadAction<{ message: ChatMessage; model: string; plugin: string; mcpTools: IMCPTool[] }>,
    ) => {},
    /** 재질의 대상 어시스턴트 메시지 비우기 */
    clearMessageContentsForRetry: (
      state: ConversationsState,
      action: PayloadAction<{ sessionId: string; messageId: string }>,
    ) => {
      const { sessionId, messageId } = action.payload;
      const target = state.conversations[sessionId]?.messages?.data?.find((m) => m.id === messageId);
      if (target) {
        target.contents = [];
        target.resultMeta = undefined;
      }
    },
    /*
     * updateUserIsTyping() and updateUserIsTypingFromServer() both update a user's typing state.
     * However they are for different purposes. The former action is for updating an user's typing state from
     * the webapp and will be captured by the SignalR middleware and the payload will be broadcasted to all clients
     * in the same group.
     * The updateUserIsTypingFromServer() action is triggered by the SignalR middleware when a state is received
     * from the webapi.
     */
    updateUserIsTyping: (state: ConversationsState, action: PayloadAction<{ chatId: string; isTyping: boolean }>) => {
      const { chatId, isTyping } = action.payload;
      updateUserTypingState(state, chatId, isTyping);
    },
    // updateUserIsTypingFromServer: (
    //     state: ConversationsState,
    //     action: PayloadAction<{ userId: string; chatId: string; isTyping: boolean }>,
    // ) => {
    //     const { userId, chatId, isTyping } = action.payload;
    //     updateUserTypingState(state, userId, chatId, isTyping);
    // },
    updateBotResponseStatus: (state: ConversationsState, action: PayloadAction<UpdateBotStatePayload>) => {
      const { chatRoomId, isActive: status } = action.payload;
      const id = chatRoomId ? chatRoomId : state.selectedId;
      const conversation = state.conversations[id];
      // popMessageContent(conversation, status);
      conversation.botResponseStatus = status;
    },
    updateBotResponseProgress: (state: ConversationsState, action: PayloadAction<IChatStreamingProgressMessage>) => {
      const { chatId, progress } = action.payload;
      const id = state.selectedId;
      const conversation = state.conversations[id];
      const index = state.conversations[state.selectedId].messages.data.findIndex((m) => m.id == chatId);
      if (index > -1) {
        const target = conversation.messages.data[index];
        target.progress = progress;
        // console.log('[progress-set]', {
        //   chatId,
        //   index,
        //   authorUserId: target.author?.userId,
        //   contentTypes: target.contents?.map(c => (c as { $type?: string }).$type ?? 'text'),
        //   isLast: index === conversation.messages.data.length - 1,
        // });
      }
    },
    stopStreaming: () => {},
    // updateMessageProperty: <K extends keyof IChatMessage, V extends IChatMessage[K]>(
    //     state: ConversationsState,
    //     action: PayloadAction<{
    //         property: K;
    //         value: V;
    //         chatId: string;
    //         messageIdOrIndex: string | number;
    //         updatedContent?: string;
    //         frontLoad?: boolean;
    //     }>,
    // ) => {
    //     const { property, value, messageIdOrIndex, chatId, updatedContent, frontLoad } = action.payload;
    //     const conversation = state.conversations[chatId];
    //     const conversationMessage =
    //         typeof messageIdOrIndex === 'number'
    //             ? conversation.messages[messageIdOrIndex]
    //             : conversation.messages.find((m) => m.id === messageIdOrIndex);

    //     if (conversationMessage) {
    //         conversationMessage[property] = value;
    //         if (updatedContent) {
    //             conversationMessage.content = updatedContent;
    //         }
    //     }

    //     if (frontLoad) {
    //         frontLoadChat(state, chatId);
    //     }
    // },
    deleteConversation: (state: ConversationsState, action: PayloadAction<ChatRoom>) => {
      const id = action.payload.id;
      const { [id]: _, ...rest } = state.conversations;
      state.conversations = rest;
    },

    resetConversation(state, action: PayloadAction<string>) {
      const chatroomId = action.payload;
      if (state.conversations[chatroomId]) state.conversations[chatroomId].messages.data = [];
    },
  },
});

// const updateConversation = (state: ConversationsState, chatId: string, message: IChatMessage) => {
//   const index = state.conversations[chatId].messages.data.findIndex((m) => m.id == chatId);
//   if(index > -1){
//     state.conversations[chatId].messages.data[index] = message;
//   }else{
//     state.conversations[chatId].messages.data.push(message);
//   }

//   // frontLoadChat(state, chatId);
// };

const isApprovalReqContent = (c: AIContent) => (c as { $type?: string }).$type === 'toolApprovalRequest';
const isApprovalResContent = (c: AIContent) => (c as { $type?: string }).$type === 'toolApprovalResponse';

/** 스트리밍에서 승인 요청으로 먼저 들어오는 도구 호출 (BE 저장 시 toolApprovalRequest 로 변환됨) */
const isApprovalToolCall = (c: AIContent) =>
  isFunctionCallContent(c) && (c as { name?: string }).name === 'request_approval';

/** 승인 건 식별 id. toolCall.callId 는 ficc_ 접두사가 없는 실행용이라 쓰지 않는다. */
const approvalRequestId = (c: AIContent) =>
  (c as { requestId?: string }).requestId ?? (c as { callId?: string }).callId;

/**
 * 승인요청/승인응답을 시간순으로 누적한다.
 * - 요청(toolApprovalRequest): 기존에 없던 승인 id 면 뒤에 추가 (카드 누적)
 * - 응답(toolApprovalResponse): 기존에 없던 승인 id 면 뒤에 추가 (승인 카드가 "승인됨" 으로 바뀌도록)
 */
const accumulateApprovals = (existing: ChatMessage, message: ChatMessage): ChatMessage => {
  const existingContents = existing.contents ?? [];
  const existingReqIds = new Set(existingContents.filter(isApprovalReqContent).map(approvalRequestId));
  const existingResIds = new Set(existingContents.filter(isApprovalResContent).map(approvalRequestId));
  const newContents = (message.contents ?? []).filter(
    (c) =>
      (isApprovalReqContent(c) && !existingReqIds.has(approvalRequestId(c))) ||
      (isApprovalResContent(c) && !existingResIds.has(approvalRequestId(c))),
  );

  // 같은 승인 건의 request_approval 이 먼저 들어와 있으면 제거 (카드 중복 방지)
  const incomingReqIds = new Set(newContents.filter(isApprovalReqContent).map(approvalRequestId));
  const kept = incomingReqIds.size
    ? existingContents.filter((c) => !(isApprovalToolCall(c) && incomingReqIds.has(approvalRequestId(c))))
    : existingContents;

  // 스냅샷이 내용을 모두 담고 있으면 순서가 정확하므로 그대로 쓴다.
  if ((message.contents?.length ?? 0) >= existingContents.length) return message;

  if (newContents.length === 0 && kept.length === existingContents.length) return existing;
  return { ...existing, contents: [...kept, ...newContents] };
};

const updateConversation = (state: ConversationsState, message: ChatMessage) => {
  // sessionId 가 없으면(BE 스냅샷에 누락 — 승인 응답 스냅샷 등) 현재 선택된 세션으로 대체
  const sessionId = message.sessionId ?? state.selectedId;
  if (sessionId && state.conversations[sessionId]) {
    const index = state.conversations[sessionId].messages.data.findLastIndex((m) => m.id == message.id);
    if (index > -1) {
      const existing = state.conversations[sessionId].messages.data[index];
      const hasApproval = (message.contents ?? []).some(isApprovalReqContent);
      state.conversations[sessionId].messages.data[index] = hasApproval
        ? accumulateApprovals(existing, message)
        : mergePreservingLocalResults(existing, message);
    } else state.conversations[sessionId].messages.data.push(message);
  }

  // frontLoadChat(state, chatId);
};

// 로컬 실행 결과 합치기.
// BE 스냅샷(incoming)에 없는 로컬 결과(functionResult)를 보존하되,
//  - 같은 callId 의 functionCall 이 incoming 에 있으면 그 call 바로 뒤에 삽입
//  - call 이 incoming 에 없으면(먼저 실행돼 끝난 도구) incoming 앞에 붙임
// 이렇게 해서 call → result 순서와 실행 시간 순서를 모두 유지한다.
const mergePreservingLocalResults = (existing: ChatMessage, incoming: ChatMessage): ChatMessage => {
  const isTool = (c: AIContent) => !!c && (isFunctionResultContent(c) || isFunctionCallContent(c));
  const localResults = existing.contents?.filter((c) => isFunctionResultContent(c)) ?? [];

  const keyOf = (c: AIContent) => `${(c as { $type?: string }).$type}:${(c as { callId?: string }).callId}`;
  const callIdOf = (c: AIContent) => (c as { callId?: string }).callId;
  const incomingContents = (incoming.contents ?? []).filter(Boolean);
  const incomingKeys = new Set(incomingContents.filter(isTool).map(keyOf));

  // incoming 에 없는 로컬 결과 + 스트리밍으로 쌓인 text 를 보존한다.
  const missing = localResults.filter((c) => !incomingKeys.has(keyOf(c)));
  const incomingHasText = incomingContents.some((c) => isTextContent(c));
  const existingContents = existing.contents ?? [];
  const localTexts = incomingHasText
    ? []
    : existingContents.filter((c) => isTextContent(c) && (c as { text?: string }).text);
  if (missing.length === 0 && localTexts.length === 0) return incoming;

  const incomingCallIds = new Set(incomingContents.filter(isFunctionCallContent).map(callIdOf));
  const isApprovalReq = (c: AIContent) => (c as { $type?: string }).$type === 'toolApprovalRequest';
  const byCallId = new Map<string | undefined, AIContent[]>();
  const leading: AIContent[] = []; // 대응 call 이 incoming 에 없어 앞에 붙일 (call+result)
  for (const c of missing) {
    if (incomingCallIds.has(callIdOf(c))) {
      // incoming 에 call 이 있으면 그 뒤에 붙일 result 로만 모음
      const arr = byCallId.get(callIdOf(c)) ?? [];
      arr.push(c);
      byCallId.set(callIdOf(c), arr);
    } else {
      // incoming 에 call 이 없으면 existing 의 call 을 찾아 result 앞에 같이 보존 (제목이 사라지지 않게)
      const ownCall = existingContents.find((e) => isFunctionCallContent(e) && callIdOf(e) === callIdOf(c));
      if (ownCall && !leading.includes(ownCall)) leading.push(ownCall);
      leading.push(c);
    }
  }

  // 보존할 승인 안내 text 는 승인요청(toolApprovalRequest) 바로 앞에 넣는다.
  // (도구 결과들 뒤 → 승인 안내 → 승인요청 순서가 되도록)
  const merged: AIContent[] = [...leading];
  let textInserted = false;
  for (const c of incomingContents) {
    if (isApprovalReq(c) && !textInserted && localTexts.length) {
      merged.push(...localTexts);
      textInserted = true;
    }
    merged.push(c);
    if (isFunctionCallContent(c)) {
      const res = byCallId.get(callIdOf(c));
      if (res) {
        merged.push(...res);
        byCallId.delete(callIdOf(c));
      }
    }
  }
  for (const arr of byCallId.values()) merged.push(...arr);
  // 승인요청이 없어 못 넣은 text 는 맨 뒤에
  if (!textInserted && localTexts.length) merged.push(...localTexts);
  return { ...incoming, contents: merged };
};

// const updateStreamingConversation = (state: ConversationsState, stream: IChatStreamingMessage) => {
//   const index = state.conversations[stream.chatRoomId].messages.data.findIndex((m) => m.id == stream.chatId);
//   if (index > -1) {
//     const message = state.conversations[stream.chatRoomId].messages.data[index].messages[stream.chatItemId];
//     if (stream.hasError) {
//       message.hasError = stream.hasError;
//       message.errorMessage = stream.errorMessage;
//     } else {
//       if (stream.message)
//         message.message += stream.message;
//       if (stream.references)
//         message.references = stream.references;
//       if (stream.searchResult)
//         message.searchResult = stream.searchResult;
//       if (stream.functionResult) {
//         (message.functionResults ??= []).push(stream.functionResult);
//       }
//     }
//   }
// };

const updateStreamingConversation = (state: ConversationsState, chatRoomId: string, stream: AgentResponseUpdate) => {
  const sessionId =
    typeof stream.additionalProperties?.sessionId === 'string' ? stream.additionalProperties.sessionId : chatRoomId;

  if (sessionId) {
    const messageId = typeof stream.additionalProperties?.id === 'string' ? stream.additionalProperties.id : null;
    const index = state.conversations[sessionId].messages.data.findIndex((m) => m.id == messageId);
    if (index > -1) {
      const message = state.conversations[sessionId].messages.data[index];
      if (message.contents && message.contents.length > 0) {
        if (stream.contents && isTextContent(stream.contents[0])) {
          const incoming = stream.contents[0];
          const isReplace =
            (incoming.additionalProperties as { messageReplace?: boolean } | undefined)?.messageReplace === true;
          const contents = message.contents[message.contents.length - 1];
          if (isTextContent(contents) && incoming.text !== '') {
            if (isReplace) {
              // 교체 플래그: 이어붙이지 않고 통째 교체
              contents.text = incoming.text;
            } else if (contents.text !== contents.text + incoming.text) {
              contents.text += incoming.text;
            }
          } else {
            message.contents.push(incoming);
          }
        } else if (stream.contents && isFunctionResultContent(stream.contents[0])) {
          message.contents.push(stream.contents[0]);
        } else if (stream.contents && isFunctionCallContent(stream.contents[0])) {
          message.contents.push(stream.contents[0]);
        }
      } else {
        message.contents = stream.contents;
      }
    }
  }
};

const updateUserTypingState = (state: ConversationsState, chatId: string, isTyping: boolean) => {
  const conversation = state.conversations[chatId];
  conversation.isTyping = isTyping;
};

// const popMessageContent = (chatRoom: IChatRoom, status: boolean) => {
//     const message = chatRoom.messages.data;
//     if(chatRoom.botResponseStatus && !status){
//         if(message[message.length-1].message.length === 0) message.pop();
//     }
// }

const clearMessageConversation = (state: ConversationsState, chatId: string) => {
  const index = state.conversations[state.selectedId].messages.data.findIndex((c) => c.id == chatId);
  if (index > -1) state.conversations[state.selectedId].messages.data.splice(index, 1);
};

export const {
  setConversations,
  setCountinuationToken,
  editConversationTitle,
  // editConversationInput,
  // editConversationSystemDescription,
  // editConversationMemoryBalance,
  setSelectedConversation,
  // toggleMultiUserConversations,
  addConversation,
  // setImportingDocumentsToConversation,
  addMessageToConversation,
  addMessageToConversationFromUser,
  addMessageToConversationFromServer,
  addStreamingMessageToConversationFromServer,
  // updateMessageProperty,
  updateUserIsTyping,
  // updateUserIsTypingFromServer,
  updateBotResponseStatus,
  // setUsersLoaded,
  deleteConversation,
  resetConversation,
  // disableConversation,
  // updatePluginState,
  leaveConversation,
  addChatMessages,
  setChatMessages,
  addPluginMessage,
  stopStreaming,
  clearMessageToConversationFromServer,
  retryMessageToConversationFromUser,
  clearMessageContentsForRetry,
  updateBotResponseProgress,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
